import React, { useCallback, useMemo, useState } from "react";
import { Image, Modal, ModalProps } from "react-bootstrap";
import { Tournament } from "@nestcoinco/hyper-api-gateway-api-client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./UnableToPerformActionModal.scss";
import ImageSadEmoji from "../../../images/sad-emoji.png";
import FilledButton, {
  ButtonSizes,
} from "../../dashboard-page/FilledButton/FilledButton";
import { ROUTES, GamePlayEvents } from "../../../utils/constants";
import { useAtom } from "jotai";
import { duelEntryAtomWithStorage } from "../../../jotai/atoms/tournament";
import GameModal from "../GameModal/GameModal";
import { showErrorToast } from "../../../utils/show-toast";
import ImageFailedLaunch from "../../../images/unable-to-launch-game.png";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import ImageSessionTimeout from "../../../images/session-timeout-icon.png";
import {
  gamePlayEventDataAtom,
  gamePlayEventNameAtom,
} from "../../../jotai/atoms/games";

export enum GameFailureActions {
  SUBMIT_SCORES = "submit-scores",
  LAUNCH_GAME = "launch-game",
}
interface UnableToPerformActionModalProps extends ModalProps {
  action?: GameFailureActions;
  tournament?: Tournament;
}

function UnableToPerformActionModal({
  tournament,
  action = GameFailureActions.SUBMIT_SCORES,
  ...restProps
}: UnableToPerformActionModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("games");
  const [duelEntry] = useAtom(duelEntryAtomWithStorage);
  const lastGamePlayUrl = duelEntry?.gamePlayUrl;
  const [replay, setReplay] = useState(false);
  const [gamePlayEvent, setGamePlayEvent] = useAtom(gamePlayEventNameAtom);
  const [gamePlayEventData, setGamePlayEventData] = useAtom(
    gamePlayEventDataAtom
  );
  const { getCurrentContent } = useCurrentContent();

  const { title, content, heroImage } = useMemo(
    () =>
      getCurrentContent({
        action,
        event: gamePlayEvent,
        data: {
          recommendedOrientation:
            gamePlayEventData?.data?.recommendedOrientation,
          recommendedPlatform: gamePlayEventData?.data?.recommendedPlatform,
        },
      }),
    [
      action,
      gamePlayEvent,
      gamePlayEventData?.data?.recommendedOrientation,
      gamePlayEventData?.data?.recommendedPlatform,
      getCurrentContent,
    ]
  );

  const resetGameEventState = useCallback(() => {
    setGamePlayEventData(undefined);
    setGamePlayEvent(undefined);
  }, [setGamePlayEvent, setGamePlayEventData]);

  const handleReplay = useCallback(() => {
    if (!lastGamePlayUrl) {
      showErrorToast("unable_to_perform_action_modal.game_not_found");
      return;
    }
    resetGameEventState();
    setReplay(true);
  }, [lastGamePlayUrl, resetGameEventState]);

  const handleReplayLater = useCallback(() => {
    resetGameEventState();
    navigate(`${ROUTES.GAMES.ROOT}`);
  }, [navigate, resetGameEventState]);

  return (
    <Modal
      {...restProps}
      size="sm"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="unable-to-perform-action"
    >
      <div className="p-3 pb-4">
        <header style={{ flexDirection: "column" }}>
          <div className="d-flex justify-content-center w-100">
            <Image src={heroImage} width={64} height={64} alt="" />
          </div>
          <Modal.Title
            id="contained-modal-title-vcenter"
            as="h1"
            className="heading w-100"
          >
            {title}
          </Modal.Title>
        </header>
        <main>
          <p className="content mb-0">{content}</p>
        </main>
        <footer className="footer-actions">
          <PrimaryActionButton
            text={t("unable_to_perform_action_modal.try_again")}
            buttonClassName="w-100"
            onClick={handleReplay}
          />
          <FilledButton
            fullwidth
            size={ButtonSizes.MD}
            className=""
            style={{ color: "#7E43FF", background: "#F5F5F50D" }}
            onClick={handleReplayLater}
          >
            {t("unable_to_perform_action_modal.replay_later")}
          </FilledButton>
        </footer>
      </div>
      {lastGamePlayUrl && replay && (
        <GameModal
          gameLink={lastGamePlayUrl}
          gameName={tournament?.game.name}
        />
      )}
    </Modal>
  );
}

export default UnableToPerformActionModal;

function useCurrentContent() {
  const { t } = useTranslation("games", {
    keyPrefix: "unable_to_perform_action_modal",
  });

  const getCurrentContent = useCallback(
    (params: {
      action: GameFailureActions;
      event?: GamePlayEvents;
      data?: { recommendedPlatform?: string; recommendedOrientation?: string };
    }) => {
      const base = {
        title: t("unable_to_launch_game"),
        content: "",
        heroImage: ImageFailedLaunch,
      };

      switch (params.event) {
        case GamePlayEvents.SDK_SESSION_TIMEOUT:
          return {
            title: t("session_timeout_title"),
            content: t("session_timeout_content"),
            heroImage: ImageSessionTimeout,
          };

        case GamePlayEvents.SDK_WRONG_PLATFORM:
          return {
            ...base,
            content: t("wrong_platform", {
              platform: params.data?.recommendedPlatform ?? "",
            }),
          };

        case GamePlayEvents.SDK_WRONG_ORIENTATION:
          return {
            ...base,
            content: t("wrong_orientation", {
              orientation: params.data?.recommendedOrientation ?? "",
            }),
          };

        case GamePlayEvents.SDK_CONNECTION_FAILED:
          return {
            title: t("unable_to_launch_game"),
            content: t("connection_lost_content"),
            heroImage: ImageFailedLaunch,
          };
      }

      if (params.action === GameFailureActions.LAUNCH_GAME) {
        return {
          ...base,
          content: t("launch_error_content"),
          heroImage: ImageFailedLaunch,
        };
      }

      return {
        title: t("unable_to_submit_scores"),
        content: t("submit_error_content"),
        heroImage: ImageSadEmoji,
      };
    },
    [t]
  );

  return { getCurrentContent };
}
